import type { Node, NodeBase } from '@/service/graph'

/**
 * ENG-2632 — the text fields the multi-select panel can batch fill/clear, as
 * semantic keys the panel emits without knowing node types. `tag` is resolved to
 * the right storage key per node type in {@link planBulkFieldUpdate}.
 */
export type BatchTextField = 'tag' | 'annotation_text' | 'note'

/** A partial node patch for a bulk update — `content` is tag-only, hence not on NodeBase. */
export type BulkFieldPatch = Partial<Omit<NodeBase, 'id' | 'type'>> & { content?: string }

/** One `updateMultipleNodes(ids, patch)` call — the selection may split by node type. */
export interface BulkFieldGroup {
  ids: string[]
  patch: BulkFieldPatch
}

/**
 * Plan the per-node-type store patches for filling (`value`) or clearing (`value === ''`)
 * a text field across a multi-selection.
 *
 * `note`/`annotation_text` map uniformly across every node type. `tag` routes by type —
 * tag nodes store their identifier text in `content`, everything else in `label` — which
 * mirrors the single-node save in NodeDetailsPanelV2 and is why a mixed selection produces
 * two groups.
 *
 * Empty is normalized to `undefined` for `label`/`note`/`annotation_text` (matching the
 * single-node panel: publishGraph then deletes the stale property row). `content` keeps the
 * literal `''` because the tag branch force-upserts `content || ''`, so `''` is what clears it.
 */
export function planBulkFieldUpdate(
  nodes: Map<string, Node>,
  ids: string[],
  field: BatchTextField,
  value: string,
): BulkFieldGroup[] {
  const norm = value === '' ? undefined : value
  if (field === 'note') return [{ ids, patch: { note: norm } }]
  if (field === 'annotation_text') return [{ ids, patch: { annotation_text: norm } }]

  // field === 'tag'
  const tagIds = ids.filter((id) => nodes.get(id)?.type === 'tag')
  const otherIds = ids.filter((id) => nodes.get(id)?.type !== 'tag')
  const groups: BulkFieldGroup[] = []
  if (otherIds.length) groups.push({ ids: otherIds, patch: { label: norm } })
  // Tag nodes: also clear the dead `label` (ENG-2595) — the tag persistence layer
  // ignores it, and NodeDetailsPanelV2's single-node save always sets it undefined,
  // so leaving a stale `label` would drift a bulk-edited tag from the single-edit path.
  if (tagIds.length) groups.push({ ids: tagIds, patch: { content: value, label: undefined } })
  return groups
}
